import { Cancel, CheckCircle } from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Stack,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import type { ContactStage } from '@otw/models/leads/contacts';
import React from 'react';
import { formatLastActivityDateTime } from '../../util/activityDisplay';
import { formatPhoneNumber } from 'utils/formatters';

const STAGE_LABEL: Record<ContactStage, string> = {
  prospect: 'Prospect',
  opportunity: 'Opportunity',
  customer: 'Customer',
  unqualified: 'Unqualified',
};

const stageAccentColor = (stage: ContactStage, palette: Theme['palette']): string => {
  if (stage === 'customer') return palette.success.main;
  if (stage === 'opportunity') return palette.info.main;
  if (stage === 'unqualified') return palette.text.secondary;
  return palette.warning.main;
};

export interface ContactListCardRow {
  id: string;
  name: string;
  email: string;
  secondaryEmail?: string;
  phone: string;
  ownerName: string;
  inventoryOwnerName: string;
  companyNames: string[];
  lastActivityAt: Date | null;
  stage: ContactStage;
  inventoryStage: ContactStage;
  marketingEmailStatus?: 'subscribed' | 'unsubscribed';
  marketingEmailSuppressed?: boolean;
  hasPortalAccount: boolean;
}

interface ContactListCardProps {
  row: ContactListCardRow;
  mergePickMode: boolean;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onPreview: (id: string) => void;
  onOpenContactPage: (id: string, event: React.MouseEvent) => void;
}

const ContactListCard: React.FC<ContactListCardProps> = ({
  row,
  mergePickMode,
  isSelected,
  onToggleSelect,
  onPreview,
  onOpenContactPage,
}) => {
  const theme = useTheme();
  const accent = stageAccentColor(row.stage, theme.palette);

  return (
    <Paper
      variant="outlined"
      onClick={() => {
        if (mergePickMode) onToggleSelect(row.id);
      }}
      sx={{
        width: '100%',
        p: 1.5,
        borderRadius: 2.5,
        cursor: mergePickMode ? 'pointer' : 'default',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        {mergePickMode ? (
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelect(row.id)}
            onClick={(event) => event.stopPropagation()}
            sx={{ p: 0.5, mt: 0.25 }}
          />
        ) : null}
        <Avatar
          onClick={(event) => onOpenContactPage(row.id, event)}
          sx={{
            width: 40,
            height: 40,
            bgcolor: alpha(accent, 0.18),
            color: accent,
            fontSize: 15,
            fontWeight: 700,
            flexShrink: 0,
            cursor: 'pointer',
          }}
        >
          {row.name?.charAt(0)?.toUpperCase() || '?'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              noWrap
              onClick={(event) => onOpenContactPage(row.id, event)}
              sx={{
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {row.name}
            </Typography>
            {row.hasPortalAccount ? (
              <CheckCircle sx={{ color: 'success.main', fontSize: 20, flexShrink: 0 }} />
            ) : (
              <Cancel sx={{ color: 'error.main', fontSize: 20, flexShrink: 0 }} />
            )}
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
            <Chip
              size="small"
              label={`Sales · ${STAGE_LABEL[row.stage]}`}
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: alpha(accent, 0.14),
                color: accent,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <Chip
              size="small"
              label={`Inventory · ${STAGE_LABEL[row.inventoryStage]}`}
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: alpha(stageAccentColor(row.inventoryStage, theme.palette), 0.14),
                color: stageAccentColor(row.inventoryStage, theme.palette),
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <Chip
              size="small"
              label={
                row.marketingEmailSuppressed
                  ? 'Suppressed'
                  : row.marketingEmailStatus === 'unsubscribed'
                    ? 'Unsubscribed'
                    : 'Subscribed'
              }
              variant={
                row.marketingEmailSuppressed || row.marketingEmailStatus === 'unsubscribed'
                  ? 'outlined'
                  : 'filled'
              }
              color={
                row.marketingEmailSuppressed || row.marketingEmailStatus === 'unsubscribed'
                  ? 'default'
                  : 'success'
              }
              sx={{ height: 20, fontSize: 11, fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }}
            />
            <Typography variant="caption" color="text.secondary" noWrap>
              Sales · {row.ownerName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Inventory · {row.inventoryOwnerName}
            </Typography>
          </Stack>
          {row.companyNames.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 0.75 }}
            >
              {row.companyNames.map((name) => (
                <Chip
                  key={name}
                  size="small"
                  label={name}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    maxWidth: 180,
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    color: theme.palette.secondary.dark,
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      px: 0.75,
                    },
                  }}
                />
              ))}
            </Stack>
          ) : null}
          {row.email ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {row.email}
            </Typography>
          ) : null}
          {row.secondaryEmail ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {row.secondaryEmail}
            </Typography>
          ) : null}
          {row.phone ? (
            <Typography variant="body2" color="text.secondary" noWrap>
              {formatPhoneNumber(row.phone)}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Last activity: {formatLastActivityDateTime(row.lastActivityAt)}
          </Typography>
          {!mergePickMode ? (
            <Button
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                onPreview(row.id);
              }}
              sx={{
                mt: 1,
                py: 0.5,
                px: 1.25,
                minWidth: 0,
                fontSize: 12,
                lineHeight: 1.4,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Preview
            </Button>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
};

export default ContactListCard;
